import { useEffect, useState } from 'react';
import { AlertCircle, BellOff, LogOut } from 'lucide-react';
import type {
  Cita,
  Cliente,
  NuevaCitaDraft,
  NuevoClienteDraft,
  Seccion,
  VistaPantalla,
} from './types';
import { useAuth } from './hooks/useAuth';
import { useCitas } from './hooks/useCitas';
import { useClientes } from './hooks/useClientes';
import { crearCanalesNotificacion, probarAlarma, probarNotificacion } from './lib/notifications';
import { nuevoUUID } from './lib/uuid';
import { arrancarSyncWorker, detenerSyncWorker } from './lib/sync';
import { BannerNotificaciones, Encabezado } from './components/Encabezado';
import { Navegacion } from './components/Navegacion';
import { EstadoSync } from './components/EstadoSync';
import { LoginScreen } from './components/LoginScreen';
import { FormularioCita } from './components/citas/FormularioCita';
import { ListaCitas } from './components/citas/ListaCitas';
import { CalendarioMensual } from './components/citas/CalendarioMensual';
import { FormularioCliente } from './components/clientes/FormularioCliente';
import { SeccionPagos } from './components/pagos/SeccionPagos';
import { SeccionTareas } from './components/tareas/SeccionTareas';
import { SeccionIdeas } from './components/ideas/SeccionIdeas';
import { DialogoConfirmacion } from './components/ui/DialogoConfirmacion';
import './App.css';

function App() {
  const { usuario, cargando: cargandoAuth, cerrarSesion } = useAuth();

  if (cargandoAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  if (!usuario) {
    return <LoginScreen />;
  }

  return <AppAutenticada onCerrarSesion={() => void cerrarSesion()} />;
}

function AppAutenticada({ onCerrarSesion }: { onCerrarSesion: () => void }) {
  const { citasProximas, citasPasadas, guardarCita, eliminarCita, silenciarCita } = useCitas();
  const { clientes, agregarCliente, buscarClientes } = useClientes();

  const [seccion, setSeccion] = useState<Seccion>('citas');
  const [vistaActual, setVistaActual] = useState<VistaPantalla>('lista');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editandoCita, setEditandoCita] = useState<Cita | null>(null);
  const [nombreClienteNuevo, setNombreClienteNuevo] = useState<string | null>(null);
  const [citaAEliminar, setCitaAEliminar] = useState<string | null>(null);
  const [citaASilenciar, setCitaASilenciar] = useState<Cita | null>(null);
  const [mesActual, setMesActual] = useState<Date>(new Date());
  const [clienteRecienAgregado, setClienteRecienAgregado] = useState<Cliente | null>(null);

  useEffect(() => {
    void crearCanalesNotificacion();
  }, []);

  useEffect(() => {
    arrancarSyncWorker();
    return () => detenerSyncWorker();
  }, []);

  const abrirFormularioNuevaCita = () => {
    setEditandoCita(null);
    setClienteRecienAgregado(null);
    setMostrarFormulario(true);
  };

  const cancelarFormulario = () => {
    setMostrarFormulario(false);
    setEditandoCita(null);
    setClienteRecienAgregado(null);
  };

  const onGuardarCita = (
    draft: NuevaCitaDraft,
    nombreCliente: string,
    clienteSeleccionado: Cliente | null
  ) => {
    const ahora = new Date().toISOString();
    const esCreate = editandoCita === null;
    const cita: Cita = {
      id: editandoCita?.id ?? nuevoUUID(),
      clienteId: clienteSeleccionado ? clienteSeleccionado.id : null,
      cliente: nombreCliente,
      telefono: draft.telefono,
      fecha: draft.fecha,
      hora: draft.hora,
      notas: draft.notas,
      notificacionesSilenciadas: editandoCita?.notificacionesSilenciadas ?? false,
      creada: editandoCita?.creada ?? ahora,
      updatedAt: ahora,
      deletedAt: null,
    };
    void guardarCita(cita, esCreate);
    cancelarFormulario();
  };

  const onGuardarClienteNuevo = async (draft: NuevoClienteDraft) => {
    const cliente = await agregarCliente(draft);
    setClienteRecienAgregado(cliente);
    setNombreClienteNuevo(null);
  };

  const confirmarEliminacion = () => {
    if (citaAEliminar !== null) {
      void eliminarCita(citaAEliminar);
    }
    setCitaAEliminar(null);
  };

  const confirmarSilenciar = () => {
    if (citaASilenciar) {
      void silenciarCita(citaASilenciar);
    }
    setCitaASilenciar(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3 px-1">
          <EstadoSync />
          <button
            type="button"
            onClick={onCerrarSesion}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
            title="Cerrar sesión"
          >
            <LogOut size={14} />
            Cerrar sesión
          </button>
        </div>
        <Navegacion seccion={seccion} onCambiar={setSeccion} />

        {seccion === 'pagos' && <SeccionPagos />}
        {seccion === 'tareas' && <SeccionTareas />}
        {seccion === 'ideas' && <SeccionIdeas />}

        {seccion === 'citas' && (
          <SeccionCitas
            clientes={clientes}
            citasProximas={citasProximas}
            citasPasadas={citasPasadas}
            vistaActual={vistaActual}
            mostrarFormulario={mostrarFormulario}
            editandoCita={editandoCita}
            clienteRecienAgregado={clienteRecienAgregado}
            nombreClienteNuevo={nombreClienteNuevo}
            citaAEliminar={citaAEliminar}
            citaASilenciar={citaASilenciar}
            mesActual={mesActual}
            buscarClientes={buscarClientes}
            onAlternarVista={() => setVistaActual(vistaActual === 'lista' ? 'calendario' : 'lista')}
            onNuevaCita={abrirFormularioNuevaCita}
            onGuardarCita={onGuardarCita}
            onCancelarFormulario={cancelarFormulario}
            onSolicitarNuevoCliente={(nombre) => setNombreClienteNuevo(nombre)}
            onGuardarClienteNuevo={onGuardarClienteNuevo}
            onCancelarClienteNuevo={() => setNombreClienteNuevo(null)}
            onConfirmarEliminacion={confirmarEliminacion}
            onCancelarEliminacion={() => setCitaAEliminar(null)}
            onConfirmarSilenciar={confirmarSilenciar}
            onCancelarSilenciar={() => setCitaASilenciar(null)}
            onEditarCita={(cita) => {
              setEditandoCita(cita);
              setMostrarFormulario(true);
            }}
            onEliminarCita={(id) => setCitaAEliminar(id)}
            onSilenciarCita={(cita) => setCitaASilenciar(cita)}
            onCambiarMes={(dir) =>
              setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + dir, 1))
            }
          />
        )}
      </div>
    </div>
  );
}

interface SeccionCitasProps {
  clientes: Cliente[];
  citasProximas: Cita[];
  citasPasadas: Cita[];
  vistaActual: VistaPantalla;
  mostrarFormulario: boolean;
  editandoCita: Cita | null;
  clienteRecienAgregado: Cliente | null;
  nombreClienteNuevo: string | null;
  citaAEliminar: string | null;
  citaASilenciar: Cita | null;
  mesActual: Date;
  buscarClientes: (texto: string) => Cliente[];
  onAlternarVista: () => void;
  onNuevaCita: () => void;
  onGuardarCita: (
    draft: NuevaCitaDraft,
    nombreCliente: string,
    clienteSeleccionado: Cliente | null
  ) => void;
  onCancelarFormulario: () => void;
  onSolicitarNuevoCliente: (nombre: string) => void;
  onGuardarClienteNuevo: (draft: NuevoClienteDraft) => Promise<void>;
  onCancelarClienteNuevo: () => void;
  onConfirmarEliminacion: () => void;
  onCancelarEliminacion: () => void;
  onConfirmarSilenciar: () => void;
  onCancelarSilenciar: () => void;
  onEditarCita: (cita: Cita) => void;
  onEliminarCita: (id: string) => void;
  onSilenciarCita: (cita: Cita) => void;
  onCambiarMes: (dir: number) => void;
}

function SeccionCitas({
  clientes,
  citasProximas,
  citasPasadas,
  vistaActual,
  mostrarFormulario,
  editandoCita,
  clienteRecienAgregado,
  nombreClienteNuevo,
  citaAEliminar,
  citaASilenciar,
  mesActual,
  buscarClientes,
  onAlternarVista,
  onNuevaCita,
  onGuardarCita,
  onCancelarFormulario,
  onSolicitarNuevoCliente,
  onGuardarClienteNuevo,
  onCancelarClienteNuevo,
  onConfirmarEliminacion,
  onCancelarEliminacion,
  onConfirmarSilenciar,
  onCancelarSilenciar,
  onEditarCita,
  onEliminarCita,
  onSilenciarCita,
  onCambiarMes,
}: SeccionCitasProps) {
  return (
    <>
      <Encabezado
        totalClientes={clientes.length}
        totalProximas={citasProximas.length}
        vistaActual={vistaActual}
        onAlternarVista={onAlternarVista}
        onProbarNotificacion={() => void probarNotificacion()}
        onProbarAlarma={() => void probarAlarma()}
        onNuevaCita={onNuevaCita}
      />

      <BannerNotificaciones />

      {mostrarFormulario && (
        <FormularioCita
          key={editandoCita?.id ?? 'nueva'}
          editando={editandoCita}
          clientePrecargado={clienteRecienAgregado}
          buscarClientes={buscarClientes}
          onGuardar={onGuardarCita}
          onCancelar={onCancelarFormulario}
          onSolicitarNuevoCliente={onSolicitarNuevoCliente}
        />
      )}

      {nombreClienteNuevo !== null && (
        <FormularioCliente
          nombreInicial={nombreClienteNuevo}
          onGuardar={onGuardarClienteNuevo}
          onCancelar={onCancelarClienteNuevo}
        />
      )}

      {citaAEliminar !== null && (
        <DialogoConfirmacion
          icono={<AlertCircle size={24} />}
          titulo="Confirmar Eliminación"
          mensaje="¿Estás seguro que deseas eliminar esta cita? Esta acción no se puede deshacer y se cancelarán todas las notificaciones asociadas."
          textoConfirmar="Sí, Eliminar"
          variante="peligro"
          onConfirmar={onConfirmarEliminacion}
          onCancelar={onCancelarEliminacion}
        />
      )}

      {citaASilenciar && (
        <DialogoConfirmacion
          icono={<BellOff size={24} />}
          titulo="Silenciar Notificaciones"
          mensaje="¿Deseas silenciar todas las notificaciones de esta cita?"
          detalle={
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Advertencia:</strong> No recibirás ningún recordatorio de esta cita,
                incluyendo la alarma de 15 minutos.
              </p>
            </div>
          }
          textoConfirmar="Sí, Silenciar"
          iconoBotonConfirmar={<BellOff size={18} />}
          variante="advertencia"
          onConfirmar={onConfirmarSilenciar}
          onCancelar={onCancelarSilenciar}
        />
      )}

      {vistaActual === 'calendario' && (
        <CalendarioMensual
          mes={mesActual}
          citas={[...citasProximas, ...citasPasadas]}
          onCambiarMes={onCambiarMes}
        />
      )}

      {vistaActual === 'lista' && (
        <ListaCitas
          citasProximas={citasProximas}
          citasPasadas={citasPasadas}
          onEditar={onEditarCita}
          onEliminar={onEliminarCita}
          onSilenciar={onSilenciarCita}
        />
      )}
    </>
  );
}

export default App;
